import { initialTaskSync, readAfterInitialTaskSync } from './initialTaskSync';
import { TaskEventVersions } from './taskEventVersions';
import { IPC_CHANNELS } from '../../electron/ipc/channels';
import { apiRequest, ApiError, jsonBody } from './api';
import { createResultExportAbortRegistry } from './resultExportSession';
import { captureSessionEpoch, isSessionEpochCurrent } from './sessionEpoch';
import { outputVariantPath } from './outputVariant';
import { createSharedUploadRegistry, type SharedUpload } from './sharedUploads';

const fileRegistry = new Map<string, File>();
const draftMediaRegistry = new Map<string,{id:string;leaseId:string;draftId:string;refreshAt:number}>();
const draftMediaRefreshes = new Map<string,Promise<void>>();
export function registerDraftMedia(draftId:string,media:Array<{path:string;id?:string;leaseId?:string}>):void {
  for(const item of media) if(item.id && item.leaseId) draftMediaRegistry.set(item.path,{id:item.id,leaseId:item.leaseId,draftId,refreshAt:Date.now()+60_000});
}
async function refreshDraftMedia(draftId:string,epoch:number):Promise<void> {
  const existing=draftMediaRefreshes.get(draftId);
  if(existing) return existing;
  const pending=(async()=>{
    const draft=await apiRequest<{media:Array<{path:string;id?:string;leaseId?:string}>}>(`/api/tasks/drafts/${draftId}/restore`,{method:'POST'});
    if(!isSessionEpochCurrent(epoch)) throw new Error('账号会话已切换');
    for(const [path,item] of draftMediaRegistry) if(item.draftId===draftId) draftMediaRegistry.delete(path);
    registerDraftMedia(draftId,draft.media);
  })();
  draftMediaRefreshes.set(draftId,pending);
  try {await pending;} finally {if(draftMediaRefreshes.get(draftId)===pending)draftMediaRefreshes.delete(draftId);}
}
const channelListeners = new Map<string, Set<(...args: any[]) => void>>();
const WEB_MAX_UPLOAD_BYTES = 20 * 1024 * 1024;
const WEB_ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const WEB_MAX_RESULT_EXPORT_FILES = 100;
const taskEventVersions = new TaskEventVersions();
let eventSource: EventSource | null = null;
let eventSourceGeneration = 0;
let eventSourceReconnectTimer: number | undefined;
let webBridgeSessionActive = false;
let webBridgeOwnerId: string | null = null;
let webResultExportInFlight = false;
const webResultExportAbortRegistry = createResultExportAbortRegistry();
const sharedUploads = createSharedUploadRegistry(async (file,signal) => {
  if (file.size > WEB_MAX_UPLOAD_BYTES || !WEB_ALLOWED_IMAGE_TYPES.has(file.type)) {
    throw new Error('仅支持不超过 20MB 的 JPG、PNG、WebP 图片');
  }
  const form = new FormData();
  form.append('image',file,file.name);
  return apiRequest<SharedUpload>('/api/files/uploads',{method:'POST',body:form,signal});
},captureSessionEpoch,isSessionEpochCurrent);

export function installWebBridge(): void {
  if (window.electron) return;
  window.IPC_CHANNELS = IPC_CHANNELS;
  window.CLOUD_RUNTIME = true;
  window.electron = {
    invoke: invokeChannel,
    getPathForFile: registerFile,
    on: (channel, callback) => {
      const listeners = channelListeners.get(channel) || new Set();
      listeners.add(callback);
      channelListeners.set(channel, listeners);
      if (webBridgeSessionActive) ensureEventSource();
      return callback;
    },
    off: (channel, callback) => {
      channelListeners.get(channel)?.delete(callback);
    },
  };
}

function registerFile(file: File): string {
  const token = URL.createObjectURL(file);
  fileRegistry.set(token, file);
  return token;
}

async function invokeChannel(channel: string, ...args: any[]): Promise<any> {
  const input = args[0] || {};
  switch (channel) {
    case IPC_CHANNELS.MODEL_LIST:
      return apiRequest('/api/library/models');
    case IPC_CHANNELS.MODEL_GET:
      return apiRequest(`/api/library/models/${input.id}`);
    case IPC_CHANNELS.MODEL_CREATE:
      return createModel(input.filePath, input.metadata || {});
    case IPC_CHANNELS.MODEL_UPDATE:
      return apiRequest(`/api/library/models/${input.id}`, { method: 'PATCH', ...jsonBody(normalizeModelPayload(input.updates)) });
    case IPC_CHANNELS.MODEL_DELETE:
      await apiRequest(`/api/library/models/${input.id}`, { method: 'DELETE' });
      return { success: true };
    case IPC_CHANNELS.PROMPT_LIST:
      return apiRequest('/api/library/prompts');
    case IPC_CHANNELS.PROMPT_GET:
      return apiRequest(`/api/library/prompts/${input.id}`);
    case IPC_CHANNELS.PROMPT_CREATE:
      return createPrompt(input.prompt || {});
    case IPC_CHANNELS.PROMPT_UPDATE:
      return apiRequest(`/api/library/prompts/${input.id}`, { method: 'PATCH', ...jsonBody(input.updates) });
    case IPC_CHANNELS.PROMPT_DELETE:
      await apiRequest(`/api/library/prompts/${input.id}`, { method: 'DELETE' });
      return { success: true };
    case IPC_CHANNELS.PROMPT_IMPORT_DEFAULTS:
      return apiRequest('/api/library/prompts');
    case IPC_CHANNELS.PROMPT_BUILD_TASK:
      return apiRequest('/api/system/prompt/build', { method: 'POST', ...jsonBody(input) });
    case IPC_CHANNELS.TASK_LIST:
      return readAfterInitialTaskSync(() => apiRequest('/api/tasks'));
    case IPC_CHANNELS.TASK_CREATE:
      return createTask(input);
    case IPC_CHANNELS.TASK_CANCEL:
      return apiRequest(`/api/tasks/${input.id}/cancel`, { method: 'POST', ...jsonBody({}) });
    case IPC_CHANNELS.TASK_RETRY:
      return apiRequest(`/api/tasks/${input.id}/retry`, { method: 'POST', ...jsonBody({ outputRoles: input.outputRoles }) });
    case IPC_CHANNELS.TASK_DELETE:
    case IPC_CHANNELS.RESULT_DELETE:
      await apiRequest(`/api/tasks/${input.id}`, { method: 'DELETE' });
      return { success: true };
    case IPC_CHANNELS.TASK_CLEAR_COMPLETED:
      return apiRequest('/api/tasks/completed/all', { method: 'DELETE' });
    case IPC_CHANNELS.RESULT_LIST:
      return readAfterInitialTaskSync(() => apiRequest('/api/results'));
    case IPC_CHANNELS.RESULT_GET:
      return apiRequest(`/api/tasks/${input.id}`);
    case IPC_CHANNELS.RESULT_DELETE_OUTPUTS:
      return apiRequest('/api/results/outputs', { method: 'DELETE', ...jsonBody({ outputIds: input.outputIds }) });
    case IPC_CHANNELS.RESULT_EXPORT:
      return exportWebResults(input);
    case IPC_CHANNELS.RESULT_OPEN_DIR:
      return { success: false, message: '网页版请使用下载按钮' };
    case IPC_CHANNELS.SETTINGS_GET_ALL:
      return apiRequest('/api/system/settings');
    case IPC_CHANNELS.SETTINGS_GET: {
      const settings = await apiRequest<Record<string, unknown>>('/api/system/settings');
      return settings[input.key];
    }
    case IPC_CHANNELS.SETTINGS_SET:
      return { success: true };
    case IPC_CHANNELS.FILE_OPEN_DIALOG:
      return selectBrowserFiles(Boolean(input?.properties?.includes('multiSelections')));
    case IPC_CHANNELS.FILE_SAVE_PASTED_IMAGE: {
      const bytes = input.data instanceof Uint8Array ? input.data : new Uint8Array(input.data || []);
      return { filePath: registerFile(new File([bytes], input.name || 'pasted-image.png', { type: input.mimeType || 'image/png' })) };
    }
    case IPC_CHANNELS.IMAGE_GET_INFO:
      return imageInfo(input.filePath);
    case IPC_CHANNELS.IMAGE_THUMBNAIL:
      return outputVariantPath(input.filePath,input.purpose === 'result' ? 'preview' : 'thumbnail');
    case IPC_CHANNELS.MODEL_HEALTH_GET:
      try {
        const data = await apiRequest(`/api/system/health?refresh=${Boolean(input.forceRefresh)}`);
        return { success: true, data };
      } catch (error) {
        return { success: false, error: error instanceof Error ? error.message : '状态获取失败' };
      }
    case IPC_CHANNELS.AI_GET_PROVIDERS:
      return [];
    case IPC_CHANNELS.AI_TEST_CONNECTION:
      return { success: true, message: '服务端统一管理连接' };
    default:
      throw new Error(`网页版暂不支持操作：${channel}`);
  }
}

export async function prepareWebTaskPayload(params: Record<string, any>) {
  const epoch = captureSessionEpoch();
  const inputFiles = await Promise.all((params.inputFiles || []).map(async (input: { filePath: string; role: string }) => {
    let restored = draftMediaRegistry.get(input.filePath);
    if(restored) {
      if(restored.refreshAt <= Date.now()) {
        await refreshDraftMedia(restored.draftId,epoch);
        restored = draftMediaRegistry.get(input.filePath);
        if(!restored) throw new Error('草稿图片已不可用，请重新打开草稿或选择图片');
      }
      return {role:input.role,mediaAsset:{id:restored.id,leaseId:restored.leaseId}};
    }
    const file = fileRegistry.get(input.filePath);
    if (file) {
      const asset=await sharedUploads.get(file);
      return {role:input.role,mediaAsset:{id:asset.id,leaseId:asset.leaseId,originalName:file.name}};
    }
    const library = parseLibraryModelPath(input.filePath);
    if (library) return { role: input.role, libraryModel: library };
    const taskOutput = parseTaskOutputPath(input.filePath);
    if (taskOutput) return { role: input.role, taskOutput };
    throw new Error('任务包含已失效的本地图片，请重新选择');
  }));
  if (!isSessionEpochCurrent(epoch)) throw new Error('账号会话已切换，已停止创建任务');
  return { ...params, inputFiles };
}

async function createTask(params: Record<string, any>, repairUpload = true): Promise<any> {
  const epoch=captureSessionEpoch();
  const form = new FormData();
  const payload = await prepareWebTaskPayload(params);
  const inputFiles = payload.inputFiles;
  form.append('payload', JSON.stringify(payload));
  let task: any;
  try {
    task = await apiRequest<any>('/api/tasks', { method: 'POST', body: form });
  } catch (error) {
    // These two errors are emitted before task admission or points reservation.
    // Never replay an ambiguous HTTP failure after a task may have committed.
    if (repairUpload && error instanceof ApiError && error.status===410
      && ['MEDIA_LEASE_EXPIRED','MEDIA_FILE_MISSING'].includes(error.payload.code)
      && isSessionEpochCurrent(epoch)) {
      for(const item of inputFiles) if(item.mediaAsset) await sharedUploads.forget(item.mediaAsset.id);
      if (!isSessionEpochCurrent(epoch)) throw new Error('账号会话已切换，已停止创建任务');
      return createTask(params,false);
    }
    throw error;
  }
  window.dispatchEvent(new CustomEvent('cloud-account-refresh'));
  return task;
}

async function createModel(filePath: string, metadata: Record<string, any>) {
  const file = fileRegistry.get(filePath);
  if (!file) throw new Error('上传图片已失效，请重新选择');
  const payload = normalizeModelPayload(metadata);
  const form = new FormData();
  form.append('payload', JSON.stringify(payload));
  form.append('original', file, file.name);
  try {
    const model = await apiRequest<any>('/api/library/models', { method: 'POST', body: form });
    releaseFile(filePath);
    return model;
  } catch (error) {
    if (!(error instanceof ApiError) || error.payload.code !== 'REVIEW_REQUIRED') throw error;
    const submission = new FormData();
    submission.append('payload', JSON.stringify({ resourceType: 'model', data: payload }));
    submission.append('original', file, file.name);
    await apiRequest('/api/library/submissions', { method: 'POST', body: submission });
    releaseFile(filePath);
    throw new Error('已提交管理员审核，审核通过后会进入共享模特库');
  }
}

async function createPrompt(prompt: Record<string, any>) {
  const payload = {
    name: prompt.name || '未命名模板',
    category: prompt.category || 'general',
    prompt: prompt.prompt || '',
    negativePrompt: prompt.negativePrompt,
  };
  try {
    return await apiRequest('/api/library/prompts', { method: 'POST', ...jsonBody(payload) });
  } catch (error) {
    if (!(error instanceof ApiError) || error.payload.code !== 'REVIEW_REQUIRED') throw error;
    await apiRequest('/api/library/submissions', {
      method: 'POST',
      ...jsonBody({ resourceType: 'prompt', data: payload }),
    });
    throw new Error('已提交管理员审核，审核通过后会进入共享提示词库');
  }
}

function normalizeModelPayload(metadata: Record<string, any>) {
  return {
    name: metadata.name || '未命名模特',
    gender: metadata.gender || 'other',
    tags: Array.isArray(metadata.tags) ? metadata.tags : [],
    metadata: metadata.metadata || {},
  };
}

function parseLibraryModelPath(filePath: string): { id: string; variant: 'original' | 'fullbody' } | null {
  const match = filePath.match(/^\/api\/files\/models\/([0-9a-f-]+)\/(original|preview|fullbody)(?:\?v=[a-z0-9%_-]+)?$/i);
  if (!match) return null;
  return { id: match[1]!, variant: match[2] === 'fullbody' ? 'fullbody' : 'original' };
}

function parseTaskOutputPath(filePath: string): { id: string; versionId?: string } | null {
  const version = filePath.match(/^\/api\/files\/outputs\/([0-9a-f-]+)\/versions\/([0-9a-f-]+)\/(original|preview|thumbnail)$/i);
  if (version) return { id: version[1]!, versionId: version[2]! };
  const match = filePath.match(/^\/api\/files\/outputs\/([0-9a-f-]+)$/i);
  return match ? { id: match[1]! } : null;
}

async function selectBrowserFiles(multiple: boolean): Promise<string[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/webp';
    input.multiple = multiple;
    input.style.display = 'none';
    const cleanup = () => input.remove();
    input.addEventListener('change', () => {
      const tokens = Array.from(input.files || []).map(registerFile);
      cleanup();
      resolve(tokens);
    }, { once: true });
    input.addEventListener('cancel', () => { cleanup(); resolve([]); }, { once: true });
    document.body.appendChild(input);
    input.click();
  });
}

async function imageInfo(filePath: string) {
  const file = fileRegistry.get(filePath);
  if (!file) {
    if (/^\/api\/files\//.test(filePath)) return { width: 0, height: 0, format: 'remote' };
    throw new Error('图片已失效，请重新选择');
  }
  if (file.size <= 0) throw new Error('图片文件为空，请重新选择');
  if (file.size > WEB_MAX_UPLOAD_BYTES) throw new Error('图片过大，请使用 20MB 以内的图片');
  if (!WEB_ALLOWED_IMAGE_TYPES.has(file.type)) throw new Error('仅支持 JPG、PNG、WebP 图片');
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error('图片文件损坏或格式不受支持');
  }
  if (!bitmap.width || !bitmap.height) {
    bitmap.close();
    throw new Error('无法读取图片尺寸');
  }
  const result = { width: bitmap.width, height: bitmap.height, format: file.type, fileSize: file.size };
  bitmap.close();
  return result;
}

function triggerDownload(url: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = '';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

interface WebResultExportManifest {
  count: number;
  totalBytes: number;
  entries: Array<{
    id: string;
    name: string;
    fileSize: number;
    mimeType: string;
    url: string;
  }>;
}

async function exportWebResults(input: {
  taskId?: string;
  outputIds?: string[];
  mode?: 'folder' | 'zip';
}) {
  if (webResultExportInFlight) throw new Error('已有结果导出正在进行，请稍候');
  webResultExportInFlight = true;
  const sessionEpoch = captureSessionEpoch();
  const sessionAbort = createSessionExportAbort(sessionEpoch);
  const unregisterAbort = webResultExportAbortRegistry.register(sessionAbort.controller);
  try {
    return await performWebResultExport(input, sessionEpoch, sessionAbort.signal);
  } finally {
    unregisterAbort();
    sessionAbort.dispose();
    webResultExportInFlight = false;
  }
}

async function performWebResultExport(input: {
  taskId?: string;
  outputIds?: string[];
  mode?: 'folder' | 'zip';
}, sessionEpoch: ReturnType<typeof captureSessionEpoch>, signal: AbortSignal) {
  const outputIds = [...new Set(input.outputIds || [])];
  if (outputIds.length === 0) {
    if (input.taskId && input.mode !== 'folder') {
      triggerDownload(`/api/results/${input.taskId}/download.zip`);
      return { success: true, exported: ['download.zip'], mode: 'zip' };
    }
    throw new Error('请先选择要导出的图片');
  }
  if (outputIds.length > WEB_MAX_RESULT_EXPORT_FILES) {
    throw new Error(`单次最多导出 ${WEB_MAX_RESULT_EXPORT_FILES} 张图片`);
  }

  if (input.mode === 'folder') {
    const showDirectoryPicker = (window as unknown as {
      showDirectoryPicker?: () => Promise<unknown>;
    }).showDirectoryPicker;
    if (!showDirectoryPicker) {
      const zipResult = await exportWebZip(outputIds, sessionEpoch, signal);
      return {
        ...zipResult,
        fallback: 'zip',
        message: '当前浏览器不支持保存到文件夹，已改为下载 ZIP',
      };
    }

    let directory: unknown;
    try {
      // Directory pickers require the call to happen while the click still has
      // transient user activation, so it intentionally runs before API awaits.
      directory = await showDirectoryPicker.call(window);
    } catch (error) {
      if (isAbortError(error)) return { success: false, cancelled: true, exported: [] };
      throw error;
    }
    const manifest = await requestWebResultManifest(outputIds, signal);
    assertExportSession(sessionEpoch);
    const exported = await withResultFolderLock(() => writeManifestToDirectory(directory, manifest, sessionEpoch, signal));
    assertExportSession(sessionEpoch);
    return { success: true, exported, mode: 'folder', totalBytes: manifest.totalBytes };
  }

  return exportWebZip(outputIds, sessionEpoch, signal);
}

async function exportWebZip(outputIds: string[], sessionEpoch: ReturnType<typeof captureSessionEpoch>, signal: AbortSignal) {
  const manifest = await requestWebResultManifest(outputIds, signal);
  assertExportSession(sessionEpoch);
  const query = new URLSearchParams({ outputs: manifest.entries.map((entry) => entry.id).join(',') });
  triggerDownload(`/api/results/download.zip?${query.toString()}`);
  return {
    success: true,
    exported: ['AI试衣结果.zip'],
    mode: 'zip',
    totalBytes: manifest.totalBytes,
  };
}

function requestWebResultManifest(outputIds: string[], signal: AbortSignal): Promise<WebResultExportManifest> {
  return apiRequest('/api/results/export-manifest', {
    method: 'POST',
    signal,
    ...jsonBody({ outputIds }),
  });
}

async function writeManifestToDirectory(
  directory: unknown,
  manifest: WebResultExportManifest,
  sessionEpoch: ReturnType<typeof captureSessionEpoch>,
  signal: AbortSignal,
): Promise<string[]> {
  const parentHandle = directory as {
    getDirectoryHandle: (name: string, options: { create: true }) => Promise<unknown>;
  };
  const directoryHandle = await parentHandle.getDirectoryHandle(crypto.randomUUID(), { create: true }) as {
    getFileHandle: (name: string, options?: { create?: boolean }) => Promise<{
      createWritable: () => Promise<WritableStream<BufferSource | Blob | string>>;
    }>;
  };
  const exported: string[] = [];
  for (const entry of manifest.entries) {
    assertExportSession(sessionEpoch);
    const response = await fetch(entry.url, {
      signal,
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: entry.mimeType },
    });
    if (!response.ok) throw await webDownloadError(response);
    const fileHandle = await directoryHandle.getFileHandle(entry.name, { create: true });
    const writable = await fileHandle.createWritable();
    try {
      if (response.body) {
        await response.body.pipeTo(writable as WritableStream<Uint8Array>, { signal });
      } else {
        await writeBlobAndClose(writable, await response.blob());
      }
    } catch (error) {
      await writable.abort(error).catch(() => undefined);
      throw error;
    }
    exported.push(entry.name);
  }
  return exported;
}

async function writeBlobAndClose(
  writable: WritableStream<BufferSource | Blob | string>,
  blob: Blob,
): Promise<void> {
  const writer = writable.getWriter();
  try {
    await writer.write(blob);
    await writer.close();
  } finally {
    writer.releaseLock();
  }
}

async function webDownloadError(response: Response): Promise<Error> {
  try {
    const payload = await response.json();
    if (payload?.error?.message) return new Error(payload.error.message);
  } catch {
    // Fall through to the status based message.
  }
  return new Error(`保存结果失败 (${response.status})`);
}

function isAbortError(error: unknown): boolean {
  return errorName(error) === 'AbortError';
}

function errorName(error: unknown): string | undefined {
  return error && typeof error === 'object' && 'name' in error
    ? String(error.name)
    : undefined;
}

async function withResultFolderLock<T>(operation: () => Promise<T>): Promise<T> {
  if (!navigator.locks) return operation();
  return navigator.locks.request(
    'ai-fashion-result-folder-export',
    { mode: 'exclusive', ifAvailable: true },
    async (lock) => {
      if (!lock) throw new Error('另一个页面正在保存结果到文件夹，请稍后重试');
      return operation();
    },
  );
}

function assertExportSession(epoch: ReturnType<typeof captureSessionEpoch>): void {
  if (!isSessionEpochCurrent(epoch)) throw new Error('账号会话已切换，已停止本次导出');
}

function createSessionExportAbort(epoch: ReturnType<typeof captureSessionEpoch>) {
  const controller = new AbortController();
  const timer = window.setInterval(() => {
    if (!isSessionEpochCurrent(epoch)) controller.abort(new DOMException('账号会话已切换', 'AbortError'));
  }, 100);
  return {
    controller,
    signal: controller.signal,
    dispose: () => window.clearInterval(timer),
  };
}

export function isWebTaskEventStreamConnected(): boolean {
  return Boolean(eventSource && eventSource.readyState === EventSource.OPEN);
}

export function isWebBridgeOwner(ownerId: string): boolean {
  return webBridgeSessionActive && webBridgeOwnerId === ownerId;
}

export async function forgetWebTaskMedia(ids: string[]): Promise<void> {
  for (const id of ids) await sharedUploads.forget(id);
}

export function startWebBridgeSession(ownerId?: string): void {
  if (ownerId) webBridgeOwnerId = ownerId;
  if (!webBridgeSessionActive) initialTaskSync.start();
  webBridgeSessionActive = true;
  ensureEventSource();
}

export function closeWebBridgeSession(): void {
  webBridgeOwnerId = null;
  initialTaskSync.close();
  sharedUploads.clear();
  for (const url of fileRegistry.keys()) URL.revokeObjectURL(url);
  fileRegistry.clear();
  draftMediaRegistry.clear();
  draftMediaRefreshes.clear();
  webBridgeSessionActive = false;
  webResultExportAbortRegistry.abortActive(new DOMException('账号会话已关闭', 'AbortError'));
  ++eventSourceGeneration;
  taskEventVersions.clear();
  if (eventSourceReconnectTimer != null) {
    window.clearTimeout(eventSourceReconnectTimer);
    eventSourceReconnectTimer = undefined;
  }
  const source = eventSource;
  eventSource = null;
  if (!source) return;
  source.onopen = null;
  source.onmessage = null;
  source.onerror = null;
  source.close();
}

function ensureEventSource(): void {
  if (eventSource || !webBridgeSessionActive || !window.CLOUD_RUNTIME) return;
  const generation = eventSourceGeneration;
  const source = new EventSource('/api/events');
  eventSource = source;
  const resync = (refreshLists = true) => {
    if (eventSource === source && generation === eventSourceGeneration && webBridgeSessionActive) {
      // The initial authenticated load is already authoritative. A later reconnect
      // may have missed changes, so only that path rereads lists and account state.
      if (refreshLists) {
        emit(IPC_CHANNELS.TASK_QUEUE_COMPLETE, { reason: 'event-stream-connected' });
        window.dispatchEvent(new CustomEvent('cloud-account-refresh'));
      }
    }
  };
  source.onopen = () => {
    if (eventSource !== source || generation !== eventSourceGeneration || !webBridgeSessionActive) return;
    resync(initialTaskSync.connected());
  };
  source.addEventListener('resync', () => resync());
  source.onmessage = (message) => {
    if (eventSource !== source || generation !== eventSourceGeneration || !webBridgeSessionActive) return;
    let event: Record<string, any>;
    try {
      event = JSON.parse(message.data) as Record<string, any>;
    } catch {
      return;
    }
    if (!event || !taskEventVersions.accept(event)) return;
    // Notifications may arrive after a newer HTTP snapshot or across retries.
    // Treat them as invalidations; only authenticated reads publish cloud state.
    emit(IPC_CHANNELS.TASK_QUEUE_COMPLETE, event);
    if (['success', 'partial_success', 'failed', 'cancelled'].includes(event.status)) {
      window.dispatchEvent(new CustomEvent('cloud-account-refresh'));
    }
  };
  source.onerror = () => {
    if (eventSource !== source || generation !== eventSourceGeneration || !webBridgeSessionActive) return;
    if (source.readyState === EventSource.CLOSED) {
      source.close();
      eventSource = null;
      eventSourceReconnectTimer = window.setTimeout(() => {
        eventSourceReconnectTimer = undefined;
        if (generation === eventSourceGeneration && webBridgeSessionActive) ensureEventSource();
      }, 2_000);
    }
  };
}

function emit(channel: string, value: unknown): void {
  channelListeners.get(channel)?.forEach((listener) => listener(value));
}

function releaseFile(token: string): void {
  if (!fileRegistry.delete(token)) return;
  URL.revokeObjectURL(token);
}
